import { Component, ChangeDetectionStrategy, computed, inject, input, output } from '@angular/core';
import { WORKFLOW_ENTITY_CATALOG } from '@formrails/workflow-designer/data-access';

interface PlaceholderGroup {
  label: string;
  tokens: { display: string; insert: string }[];
}

/**
 * Shows the `{{token}}` placeholders available to a payload/URL/response
 * template. Sources entity properties from the host-supplied entity catalog
 * and the node's own `requiredMetadata`. Emits the full `{{...}}` token on
 * click so the consumer can insert at the cursor.
 */
@Component({
  selector: 'app-placeholder-picker',
  standalone: true,
  imports: [],
  templateUrl: './placeholder-picker.component.html',
  styleUrl: './placeholder-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlaceholderPickerComponent {
  private readonly catalog = inject(WORKFLOW_ENTITY_CATALOG);

  /** Plain metadata keys declared on the node (rendered as `{{Key}}`). */
  readonly metadataKeys = input<string[]>([]);
  /** When true, add `{{Response}}` and `{{Response.<Property>}}` shortcuts. */
  readonly includeResponse = input<boolean>(false);

  readonly insert = output<string>();

  readonly groups = computed<PlaceholderGroup[]>(() => {
    const groups: PlaceholderGroup[] = [];

    const meta = this.metadataKeys();
    if (meta.length > 0) {
      groups.push({
        label: 'Metadata',
        tokens: meta.map(k => ({ display: k, insert: `{{${k}}}` }))
      });
    }

    for (const entity of this.catalog().entities) {
      groups.push({
        label: entity.name,
        tokens: entity.properties.map(p => ({
          display: `${entity.name}.${p.name}`,
          insert: `{{${entity.name}.${p.name}}}`
        }))
      });
    }

    if (this.includeResponse()) {
      groups.push({
        label: 'Response',
        tokens: [{ display: 'Response (raw)', insert: '{{Response}}' }]
      });
    }

    return groups;
  });

  onPick(token: string): void {
    this.insert.emit(token);
  }
}
